import React, { useMemo, useState } from "react";
import {
  AvailabilityCalendar,
  AvailabilityCalendarSkeleton,
  CalendarBookingModal,
  ConfirmDialog,
  NavbarContainer,
  PublicPageGuideModal,
  Skeleton,
} from "@/components";
import type {
  CalendarSelectionRange,
  SelectionActivatePayload,
} from "@/components";
import {
  CALENDAR_PAGE_GUIDE_ACTION_TRACK_SUFFIX,
  CALENDAR_PAGE_GUIDE_CONFIRM_LABEL,
  CALENDAR_PAGE_GUIDE_INTRO,
  CALENDAR_PAGE_GUIDE_STEPS,
  CALENDAR_PAGE_GUIDE_TITLE,
  CALENDAR_PAGE_LEDE,
  getCalendarPageGuideStorageKey,
} from "@/data/public/calendarPage";
import { useAvailabilityCalendar, usePublicPageGuide, useTenant } from "@/hooks";
import { useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "@/services";
import "./SpaceCalendar.scss";

type PendingSelection = {
  range: CalendarSelectionRange;
  hasConflict: boolean;
} | null;

/**
 * Public tenant page to check room availability via drag calendar
 */
const SpaceCalendar: React.FC = () => {
  const queryClient = useQueryClient();
  const { effectiveTenantSlug, space } = useTenant();
  const guideStorageKey = useMemo(
    () => getCalendarPageGuideStorageKey(effectiveTenantSlug),
    [effectiveTenantSlug],
  );
  const [selectionRange, setSelectionRange] =
    useState<CalendarSelectionRange | null>(null);
  const [pendingSelection, setPendingSelection] =
    useState<PendingSelection>(null);
  const [bookingRange, setBookingRange] =
    useState<CalendarSelectionRange | null>(null);
  const {
    rooms,
    selectedRoomId,
    weekStart,
    setWeekStart,
    events,
    loading,
    error,
  } = useAvailabilityCalendar({
    mode: "public",
    tenantSlug: effectiveTenantSlug,
  });
  const { isGuideOpen, closeGuideForSession, dismissGuidePermanently } =
    usePublicPageGuide(guideStorageKey, !loading);

  const hasRooms = rooms.length > 0;
  const showGuideModal = hasRooms && isGuideOpen;

  const handleSelectionActivate = ({
    range,
    hasConflict,
  }: SelectionActivatePayload) => {
    if (hasConflict) {
      setPendingSelection({ range, hasConflict: true });
      return;
    }

    setBookingRange(range);
  };

  const handleBookingSuccess = () => {
    setSelectionRange(null);
    void queryClient.invalidateQueries({
      queryKey: queryKeys.calendar.publicAll(effectiveTenantSlug),
    });
  };

  const handleBookingClose = () => {
    setBookingRange(null);
  };

  if (loading) {
    return (
      <NavbarContainer title={`Agenda de ${space?.name ?? "la sala"}`}>
        <div className="tenant-availability">
          <Skeleton
            width={"50%"}
            height={20}
            className="tenant-availability__intro"
          />
          <AvailabilityCalendarSkeleton />
        </div>
      </NavbarContainer>
    );
  }

  if (error) {
    return (
      <div className="container tenant-availability">
        <p>{error.message}</p>
      </div>
    );
  }

  return (
    <NavbarContainer title={`Agenda de ${space?.name ?? "la sala"}`}>
      {hasRooms ? (
        <>
          <p className="tenant-availability__lede">{CALENDAR_PAGE_LEDE}</p>
          <AvailabilityCalendar
            events={events}
            weekStart={weekStart}
            onWeekChange={setWeekStart}
            selectionRange={selectionRange}
            onSelectionRangeChange={setSelectionRange}
            onSelectionActivate={handleSelectionActivate}
            placeholderEventTitle="Doble tap para reservar este horario"
          />
        </>
      ) : (
        <div className="tenant-availability__intro">
          No hay espacios físicos configurados para consultar horarios.
        </div>
      )}

      {pendingSelection?.hasConflict && (
        <ConfirmDialog
          open
          onOpenChange={(open) => {
            if (!open) {
              setPendingSelection(null);
            }
          }}
          title="Horario no disponible"
          description="Este horario no está disponible. Elegí uno que no coincida con otra actividad agendada."
          confirmLabel="Entendido"
          confirmVariant="primary"
          showCancel={false}
          onConfirm={() => {}}
          confirmActionTrackSufix="tenant_calendar_conflict_dismiss"
        />
      )}

      {bookingRange && (
        <CalendarBookingModal
          isOpen
          onClose={handleBookingClose}
          tenantSlug={effectiveTenantSlug}
          roomId={selectedRoomId}
          range={bookingRange}
          spaceWhatsapp={space?.contact?.whatsapp}
          onSuccess={handleBookingSuccess}
        />
      )}

      <PublicPageGuideModal
        isOpen={showGuideModal}
        onClose={closeGuideForSession}
        onConfirm={dismissGuidePermanently}
        title={CALENDAR_PAGE_GUIDE_TITLE}
        intro={CALENDAR_PAGE_GUIDE_INTRO}
        steps={CALENDAR_PAGE_GUIDE_STEPS}
        confirmLabel={CALENDAR_PAGE_GUIDE_CONFIRM_LABEL}
        actionTrackSufix={CALENDAR_PAGE_GUIDE_ACTION_TRACK_SUFFIX}
      />
    </NavbarContainer>
  );
};

export default SpaceCalendar;
